// The console's Logs view (M15-4, D-613; spec 15 §4 S22). Two fixed feeds,
// never a free-form query: the server errors the request meter kept (5xx
// answers with their request ids, so the same request can be found in the
// API's own logs) and the health alerts the collector opened. Fleet-wide, or
// for one tenant when the screen is opened from that tenant's health.

import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { DataTable, type DataTableColumn } from "../../../components/data-table";
import { Field } from "../../../components/field";
import { Select } from "../../../components/select";
import { EmptyState, ErrorState, TableSkeleton } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { Tabs } from "../../../components/tabs";
import { formatTimestamp } from "../../../lib/datetime";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import { formatMs } from "../lib/health";
import {
  getTenant,
  listLogAlerts,
  listLogErrors,
  type HealthAlert,
  type LogSince,
  type RequestErrorRow,
} from "../lib/platform-api";

export const LOG_PAGE_SIZE = 50;
type Feed = "errors" | "alerts";

export function LogsScreen({ tenantId }: { tenantId?: string | undefined }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language;
  const [feed, setFeed] = useState<Feed>("errors");
  const [since, setSince] = useState<LogSince>("24h");
  const [state, setState] = useState<"open" | "all">("all");
  const [cursors, setCursors] = useState<readonly (string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const page = cursors.length;

  const tenant = useQuery({
    queryKey: ["platform", "tenant", tenantId],
    queryFn: ({ signal }) => getTenant(tenantId ?? "", signal),
    enabled: tenantId !== undefined,
  });
  const errors = useQuery({
    queryKey: ["platform", "logs", "errors", { tenantId, since, cursor }],
    queryFn: ({ signal }) =>
      listLogErrors(
        {
          since,
          limit: LOG_PAGE_SIZE,
          ...(tenantId !== undefined ? { tenant_id: tenantId } : {}),
          ...(cursor !== undefined ? { cursor } : {}),
        },
        signal,
      ),
    enabled: feed === "errors",
    placeholderData: keepPreviousData,
  });
  const alerts = useQuery({
    queryKey: ["platform", "logs", "alerts", { tenantId, state, cursor }],
    queryFn: ({ signal }) =>
      listLogAlerts(
        {
          state,
          limit: LOG_PAGE_SIZE,
          ...(tenantId !== undefined ? { tenant_id: tenantId } : {}),
          ...(cursor !== undefined ? { cursor } : {}),
        },
        signal,
      ),
    enabled: feed === "alerts",
    placeholderData: keepPreviousData,
  });
  useSignInAgainOn401(errors.error ?? alerts.error ?? tenant.error);

  const resetPaging = () => {
    setCursors([undefined]);
  };

  const errorColumns: DataTableColumn<RequestErrorRow>[] = [
    {
      key: "time",
      header: t("admin.logs.colTime"),
      age: true,
      render: (row) => formatTimestamp(row.occurred_at, locale),
    },
    {
      key: "tenant",
      header: t("admin.logs.colTenant"),
      render: (row) =>
        row.tenant === null ? (
          t(`admin.logs.host.${row.host_kind}`)
        ) : (
          <code>{row.tenant.subdomain}</code>
        ),
    },
    {
      key: "status",
      header: t("admin.logs.colStatus"),
      kind: "status",
      render: (row) => <StatusChip variant="bad">{String(row.status)}</StatusChip>,
    },
    {
      key: "route",
      header: t("admin.logs.colRoute"),
      render: (row) => (
        <code>
          {row.method} {row.route}
        </code>
      ),
    },
    {
      key: "code",
      header: t("admin.logs.colCode"),
      render: (row) => row.problem_code ?? t("admin.health.none"),
    },
    {
      key: "duration",
      header: t("admin.logs.colDuration"),
      numeric: true,
      render: (row) => formatMs(row.duration_ms, locale),
    },
    {
      key: "request",
      header: t("admin.logs.colRequest"),
      id: true,
      render: (row) => <code data-testid="admin-log-request-id">{row.request_id}</code>,
    },
  ];

  const alertColumns: DataTableColumn<HealthAlert>[] = [
    {
      key: "time",
      header: t("admin.logs.colTime"),
      age: true,
      render: (row) => formatTimestamp(row.opened_at, locale),
    },
    {
      key: "kind",
      header: t("admin.health.colKind"),
      render: (row) => t(`admin.health.alertKind.${row.kind}`),
    },
    {
      key: "tenant",
      header: t("admin.logs.colTenant"),
      render: (row) =>
        row.tenant === null ? t("admin.logs.fleet") : <code>{row.tenant.subdomain}</code>,
    },
    {
      key: "state",
      header: t("admin.health.colState"),
      kind: "status",
      render: (row) =>
        row.resolved_at === null ? (
          <StatusChip variant="bad">{t("admin.health.alertOpen")}</StatusChip>
        ) : (
          <StatusChip variant="neutral">
            {t("admin.health.alertResolved", { time: formatTimestamp(row.resolved_at, locale) })}
          </StatusChip>
        ),
    },
    {
      key: "paged",
      header: t("admin.health.colPaged"),
      render: (row) => (row.paged ? t("admin.health.paged") : t("admin.health.held")),
    },
  ];

  const active = feed === "errors" ? errors : alerts;
  const nextCursor = active.data?.next_cursor ?? null;
  const settled = !active.isPlaceholderData && !active.isFetching;
  const pager = {
    range: t("admin.tenants.page", { page }),
    page,
    onPage: (next: number) => {
      if (next > page && settled && nextCursor !== null) setCursors([...cursors, nextCursor]);
      else if (next < page) setCursors(cursors.slice(0, -1));
    },
    prevLabel: t("admin.tenants.prev"),
    nextLabel: t("admin.tenants.next"),
    hasPrev: page > 1,
    hasNext: settled && nextCursor !== null,
  };

  let body;
  if (active.isPending) {
    body = <TableSkeleton rows={6} cols={5} label={t("admin.logs.loading")} />;
  } else if (active.isError) {
    body = (
      <ErrorState
        title={t("admin.load.failed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void active.refetch();
        }}
        requestId={requestIdOf(active.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else if (feed === "errors" && errors.data !== undefined) {
    body =
      errors.data.items.length === 0 && page === 1 ? (
        <EmptyState title={t("admin.logs.noErrorsTitle")} body={t("admin.logs.noErrorsBody")} />
      ) : (
        <DataTable
          columns={errorColumns}
          rows={errors.data.items}
          rowKey={(row) => row.id}
          empty={null}
          caption={t("admin.logs.errors")}
          rowTestId="admin-log-error"
          pager={pager}
        />
      );
  } else if (alerts.data !== undefined) {
    body =
      alerts.data.items.length === 0 && page === 1 ? (
        <EmptyState title={t("admin.logs.noAlertsTitle")} body={t("admin.logs.noAlertsBody")} />
      ) : (
        <DataTable
          columns={alertColumns}
          rows={alerts.data.items}
          rowKey={(row) => row.id}
          empty={null}
          caption={t("admin.logs.alerts")}
          rowTestId="admin-log-alert"
          rowProps={(row) => ({ "data-kind": row.kind })}
          onRowClick={(row) => {
            if (row.tenant !== null) {
              void navigate({
                to: "/console/tenants/$tenantId/health",
                params: { tenantId: row.tenant.id },
              });
            }
          }}
          pager={pager}
        />
      );
  }

  return (
    <section className="admin-screen" data-testid="admin-logs">
      <PageHeader title={t("admin.logs.title")} lede={t("admin.logs.lede")} />
      {tenantId !== undefined ? (
        <div className="admin-filters" data-testid="admin-logs-tenant">
          <StatusChip variant="accent" dot={false}>
            {tenant.data?.tenant.name ?? t("admin.logs.oneTenant")}
          </StatusChip>
          <Button
            size="sm"
            onClick={() => {
              resetPaging();
              void navigate({ to: "/console/logs", search: {} });
            }}
          >
            {t("admin.logs.allTenants")}
          </Button>
        </div>
      ) : null}
      <Tabs
        label={t("admin.logs.title")}
        active={feed}
        onSelect={(id) => {
          setFeed(id === "alerts" ? "alerts" : "errors");
          resetPaging();
        }}
        tabs={[
          { id: "errors", label: t("admin.logs.errors"), "data-testid": "admin-logs-tab-errors" },
          { id: "alerts", label: t("admin.logs.alerts"), "data-testid": "admin-logs-tab-alerts" },
        ]}
      />
      <div className="admin-filters">
        {feed === "errors" ? (
          <Field label={t("admin.logs.since")}>
            <Select
              value={since}
              data-testid="admin-logs-since"
              onChange={(e) => {
                const v = e.target.value;
                setSince(v === "1h" || v === "7d" ? v : "24h");
                resetPaging();
              }}
            >
              <option value="1h">{t("admin.logs.since1h")}</option>
              <option value="24h">{t("admin.logs.since24h")}</option>
              <option value="7d">{t("admin.logs.since7d")}</option>
            </Select>
          </Field>
        ) : (
          <Field label={t("admin.logs.state")}>
            <Select
              value={state}
              data-testid="admin-logs-state"
              onChange={(e) => {
                setState(e.target.value === "open" ? "open" : "all");
                resetPaging();
              }}
            >
              <option value="all">{t("admin.logs.stateAll")}</option>
              <option value="open">{t("admin.logs.stateOpen")}</option>
            </Select>
          </Field>
        )}
      </div>
      {body}
    </section>
  );
}
